/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Squares2X2Icon,
  UserIcon,
  ChatBubbleBottomCenterIcon,
  ShoppingCartIcon,
  HeartIcon
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

// @types
import { ProposalState } from "../../../types/proposal.types";
import { INavItem, LocationState, ProposalDetailSectionProps } from "./types";

// @store
import useProposalStore from "../../../store/useProposalStore";
import { useSpinnerStore } from "../../../store/useSpinnerStore";

// @helpers
import { formatPrice } from "../../../helpers/functionsUtils";

// @components
import ProfileView from "./ProfileView";
import MarketplaceView from "./MarketplaceView";
import OpinionView from "./OpinionView";

// @services
import professionalService from "../../../services/professional.service";
import { IGalleryCompany } from "@/types/professional.types";
import profileService from '../../../services/profile.service';

const ProposalDetailSection: React.FC<ProposalDetailSectionProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openSpinner } = useSpinnerStore();
  const { proposal, professionalInfo } = location.state as LocationState;
  const { updateStatus, isLoading } = useProposalStore();
  const [pageSelected, setPageSelected] = useState(1);
  const [galleryCompany, setGalleryCompany] = useState<{ id: string, value: Blob }[]>([]);
  const [profileKey, setProfileKey] = useState(0);
  const [logo, setLogo] = useState('');

  const navItems = [
    {
      onClick: () => setPageSelected(1),
      icon: <Squares2X2Icon className="w-6 h-6" />,
      label: t('requestsAndProposals.details.proposalNav'),
      pageNumber: 1
    },
    {
      onClick: () => setPageSelected(2),
      icon: <UserIcon className="w-6 h-6" />,
      label: t('requestsAndProposals.details.profileNav'),
      pageNumber: 2
    },
    {
      onClick: () => setPageSelected(3),
      icon: <ChatBubbleBottomCenterIcon className="w-6 h-6" />,
      label: t('requestsAndProposals.details.messagesNav'),
      pageNumber: 3
    },
    {
      onClick: () => setPageSelected(4),
      icon: <ShoppingCartIcon className="w-6 h-6" />,
      label: t('requestsAndProposals.details.marketplaceNav'),
      pageNumber: 4
    },
    {
      onClick: () => setPageSelected(5),
      icon: <HeartIcon className="w-6 h-6" />,
      label: t('requestsAndProposals.details.opinionsNav'),
      pageNumber: 5
    },
  ];

  const componentNav = (key: number, props: INavItem) => {
    return (
      <button
        key={key}
        onClick={() => { setGalleryCompany([]); props.onClick(); }}
        className={`flex items-center text-[14px] text-[#2C2C2C] dark:text-white hover:text-[#4EB9B4] transition gap-[10px] ${props.pageNumber === pageSelected ? 'text-[#4EB9B4] dark:text-[#4EB9B4]' : 'text-[#2C2C2C] dark:hover:text-[#4EB9B4]'}`}
      >
        {props.icon}
        <span className="font-medium whitespace-nowrap hidden lg:flex">
          {props.label}
        </span>
      </button>
    )
  }

  const handleApprove = async () => {
    if (proposal?.proposalId) {
      openSpinner(true);
      try {
        await updateStatus(proposal.proposalId, ProposalState.Approved);
        toast.success(t("requestsAndProposals.details.successAproval"));
        navigate('/requests');
        openSpinner(false);
      } catch (error) {
        console.log(error);
        openSpinner(false);
        toast.error(t("error.postData"));
      }
    }
  };

  //profile company
  const getGallery = async () => {
    if (!professionalInfo?.professionalId) return;
    openSpinner(true);
    try {
      const response = await professionalService.getGalleryForProfessional(professionalInfo?.professionalId);
      openSpinner(false);
      if (response?.length) return response?.map((image: IGalleryCompany) => downloadImage(image.professionalImageId, response.length));
    } catch (error) {
      toast.error(t("error.callList"))
      openSpinner(false);
    }
  }

  const downloadImage = async (id: string, parentLength: number) => {
    openSpinner(true);
    try {
      await professionalService.downloadImageGallery(id).then((response: any) => {
        let list = galleryCompany;
        list.push({ id: id, value: response });
        if (parentLength === list.length) setProfileKey(prevKey => prevKey + 1)
        openSpinner(false);
        return setGalleryCompany(list);
      })
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"))
    }
  }

  const getLogoForCompany = async () => {
    if (!proposal?.companyLogoId) return;
    openSpinner(true);
    try {
      await profileService.getLogoForCompany(proposal?.companyLogoId ?? '').then((response: any) => {
        return setLogo(response);
      })
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      setLogo("");
      toast.error(t("error.callList"));
    }
  }

  useEffect(() => {
    if (pageSelected === 2) {
      getLogoForCompany();
      getGallery();
    }
  }, [pageSelected])

  return (
    <div className="flex flex-col h-[90%] lg:flex-row w-full pb-5">
      <aside className="relative w-full lg:w-1/5 h-[100%] bg-white lg:mr-10 dark:bg-dark-cBackground shadow-lg dark:shadow-white p-6 pb-[20px] mt-[20px] rounded-lg flex flex-col">
        <div className="mb-6 flex items-center justify-center p-2 rounded-md bg-[#4EB9B4]">
          <h2 className="text-white font-medium text-[14px] line-clamp-2">
            {t('requestsAndProposals.details.porposal')} {proposal?.proposalCode || 'N/A'}
          </h2>
        </div>
        <nav className="flex flex-row lg:flex-col justify-evenly lg:space-y-8">
          {navItems.map((item, index) => (
            componentNav(index, item)
          ))}
        </nav>

        <div className="absolute bottom-[20px] left-5 flex justify-center lg:flex-center items-center">
          <div className="flex flex-col xl:flex-row items-center justify-center p-4 bg-[#AFAFAF] dark:bg-dark-cBackground rounded-lg dark:border border-gray-500">
            <img
              src={
                professionalInfo?.companyImage ||
                `https://eu.ui-avatars.com/api/?name=${professionalInfo?.name || 'User'}+${professionalInfo?.lastNames || ''}&size=250`
              }
              alt="Cambiedge pets Mascotas"
              className="w-[40px] h-auto rounded-full mr-3"
            />
            <div className="flex flex-col items-center justify-center">
              <h3 className="text-white font-medium text-[15px]">{professionalInfo?.name}</h3>
              
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 p-8 ">

        
        <div className="flex justify-end flex-col lg:flex-row items-center mb-6 gap-[20px]">
          <div className="text-left">
            <p className="text-[#666666] dark:text-white font-medium text-[14px]">{t('requestsAndProposals.details.price')}</p>
            <p className="text-[20px] font-medium text-[#F97068]">
              {formatPrice(proposal?.proposalValue)} cop
            </p>
          </div>
          <button
            onClick={handleApprove}
            disabled={isLoading}
            className="mt-2 px-6 py-2 bg-bgBrand text-white rounded-lg hover:bg-red-600 transition"
          >
            {t('requestsAndProposals.details.approve')}
          </button>
        </div>

        {pageSelected === 1 &&
          <div className="py-6">
            <h1 className="text-[#666666] dark:text-white text-[14px] font-medium mb-[10px]">
              {t('requestsAndProposals.details.ourProposal')}
            </h1>

            <div className="w-full border border[#666666] rounded-lg flex p-4 h-[320px] overflow-auto">
              <p className="text-[#6A6A6A] dark:text-white/70 text-[13px]">
                {proposal?.descriptionProposet}
              </p>
            </div>
          </div>
        }

        {pageSelected === 2 &&
          <div className="py-6">
            <ProfileView
              professionalInfo={professionalInfo}
              galleryCompany={galleryCompany}
              key={profileKey}
              logo={logo}
            />
          </div>
        }

        {pageSelected === 3 &&
          <div className="py-6 h-[70vh]">
            <div className='w-full flex items-center justify-center h-[50vh]'>
              <p className='flex text-[30px] text-gray-400 dark:text-white text-center pl-[20%] pr-[20%]'>
                {t("professional.messages.message")}
              </p>
            </div>
          </div>
        }

        {pageSelected === 4 &&
          <div className="py-6">
            <MarketplaceView
              professionalId={professionalInfo.professionalId}
              setPageSelected={setPageSelected}
            />
          </div>
        }

        {pageSelected === 5 &&
          <div className="py-6">
            <OpinionView />
          </div>
        }
      </main>
    </div>
  );
};

export default ProposalDetailSection;
